import { TEMPLATES } from '@league/shared/emails';
import { Card, Eyebrow, LINK_CLASS } from '@league/ui';
import { Link } from 'react-router-dom';

/**
 * WHAT THE PLATFORM HAS TO SAY TO PLAYERS, AND WHEN.
 *
 * Six templates, in the order a league actually uses them. This is the catalogue — the commissioner
 * asking "what do we say, at what point, and in whose voice".
 *
 * IT DOES NOT SHOW A RENDERED EXAMPLE, and that is deliberate. An example needs a player's name, a
 * venue and a set of standings, and the only way to show one here is to invent them — which is the
 * thing this project renders as a visible bracket rather than doing. A director sees every one of
 * these filled in with her league's real facts on the league's Emails tab, which is where a
 * template is worth reading anyway.
 *
 * THE RULE ABOVE THE LIST IS THE POINT OF THE SCREEN. The platform composes these and sends none of
 * them: the director copies the text into her own mail client so replies reach her rather than a
 * no-reply address. Somebody arriving at a screen headed "Email templates" will assume the opposite,
 * so it says so first.
 */
export function EmailTemplates() {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-6 py-8">
      <div>
        <h1 className="font-display text-3xl leading-tight">Email templates</h1>
        <p className="mt-2 max-w-[60ch] text-[15px] leading-relaxed text-ink-2">
          Six messages, written once and filled in with each league&rsquo;s own facts.
        </p>
      </div>

      {/*
        Not a warning and not an aside — the single most surprising thing about this screen, so it
        sits above the list rather than under it.
      */}
      <Card className="bg-tint-deep p-5">
        {/*
          TINTED, so the eyebrow steps up to `ink-2`. The label grey is 4.29:1 on `tint-deep` and an
          11px line gets no large-text allowance whatever it is called — see `contrast.test.ts`,
          which pins it against every ground.
        */}
        <Eyebrow ground="tinted">How these are sent</Eyebrow>
        <p className="mt-1.5 text-[15px] leading-relaxed text-ink">
          They aren&rsquo;t. The platform composes each one with the real names, dates and standings,
          and the director copies it into their own email client — so a player who replies reaches the director
          rather than a no-reply address nobody reads.
        </p>
        <p className="mt-2 text-[14px] leading-relaxed text-ink-2">
          The only mail this platform sends by itself is the six-digit sign-in code and the
          set-password link.
        </p>
      </Card>

      <div className="flex flex-col gap-3">
        {TEMPLATES.map((template, index) => (
          <Card key={template.kind} className="flex items-baseline gap-4 p-5">
            <span className="tabular font-display text-[0.95rem] text-ink-3">
              {String(index + 1).padStart(2, '0')}
            </span>
            <span className="min-w-0">
              <span className="block text-[15px] font-semibold">{template.title}</span>
              <span className="mt-0.5 block text-[14px] text-ink-2">{template.when}</span>
            </span>
          </Card>
        ))}
      </div>

      <p className="text-[14px] leading-relaxed text-ink-2">
        To read one with a real league&rsquo;s facts in it, open any league and go to{' '}
        <Link to="/hq/leagues" className={LINK_CLASS}>
          its Emails tab
        </Link>
        . Changing the wording is a code change today — there is no editor, deliberately, until
        somebody has read them against a season that actually ran.
      </p>
    </div>
  );
}
